/**
 * THE HARD RETENTION BOUND for a load-more grid.
 *
 * WHY IT EXISTS. An earlier decision recorded that "the button IS the bound:
 * nothing accumulates without an explicit user action" (D055). That is a
 * statement about INTENT, not a bound — a user can press Load More arbitrarily
 * many times, so the retained set had no ceiling and the DOM could still grow
 * without limit. Three authoritative statements require an actual ceiling:
 *
 *   SINGLE_COLLECTION_REDESIGN.md §36 "DOM growth" — "the mitigation specified is
 *     a BOUNDED ACCUMULATION with a 'back to top' and a `?page=` deep-link entry,
 *     and a measurement task to decide whether virtualisation is needed"
 *   SINGLE_COLLECTION_REDESIGN.md §44 — "Accumulated DOM | Bounded, with a
 *     measurement task before any virtualisation decision"
 *   PHASE_05 §20 / PHASE_04 §20 — "must not accumulate unbounded DOM"
 *
 * NO VIRTUALISATION. Both §36 and §44 forbid specifying windowing before a
 * measurement task, and a page cap satisfies the contract without it.
 *
 * This module imports nothing, so the bound can be unit-tested directly — the
 * hook it serves pulls in the axios instance and the `@/` alias, which a plain
 * `node --test` run cannot resolve.
 */

/**
 * 20 pages. At the 15-items-per-page the API enforces (it ignores `per_page` —
 * D059) that is **300 items** retained at most, roughly 11,000 DOM nodes by the
 * 37-nodes-per-card figure measured on this grid (75 cards = 2,809 nodes).
 * Generous for browsing by hand, and a real ceiling rather than a description of
 * user behaviour.
 */
export const DEFAULT_MAX_RETAINED_PAGES = 20;

/**
 * The next page to fetch, or `undefined` to stop.
 *
 * Order matters: the CAP is checked before the server's own `last_page`, so it
 * holds even while the server insists there is more. When the cap is what
 * stopped paging, the caller offers a `?page=` re-entry instead — which opens a
 * fresh window and releases the retained one, and is why §36 names bounded
 * accumulation, back-to-top and the deep link as one mitigation rather than
 * three.
 */
export function nextPageWithinBound(
  lastPage: { data?: { page?: { current_page?: number; last_page?: number } } } | null | undefined,
  pagesLoaded: number,
  cap: number
): number | undefined {
  if (pagesLoaded >= cap) return undefined;
  const meta = lastPage?.data?.page;
  if (!meta || typeof meta.current_page !== "number" || typeof meta.last_page !== "number") return undefined;
  return meta.current_page < meta.last_page ? meta.current_page + 1 : undefined;
}
